
import { useEffect, useRef } from 'react';

export default function StatsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const statsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          
          // Animate numbers
          const numberElement = entry.target.querySelector('.stat-number');
          if (numberElement) {
            const finalNumber = numberElement.textContent;
            const isPercentage = finalNumber?.includes('%');
            const isDays = finalNumber?.includes('Days');
            const isTime = finalNumber?.includes('/');
            
            let targetNumber = 0;
            if (finalNumber) {
              if (finalNumber.includes('$')) {
                targetNumber = parseInt(finalNumber.replace(/[$M+,]/g, ''));
              } else if (finalNumber.includes('+')) {
                targetNumber = parseInt(finalNumber.replace(/[+,]/g, ''));
              } else if (isPercentage) {
                targetNumber = parseInt(finalNumber.replace('%', ''));
              } else if (isDays) {
                targetNumber = parseInt(finalNumber.replace(' Days', ''));
              } else if (isTime) {
                return; // Skip animation for 24/7
              }
            }

            let currentNumber = 0;
            const increment = targetNumber / 30;
            const timer = setInterval(() => {
              currentNumber += increment;
              if (currentNumber >= targetNumber) {
                currentNumber = targetNumber;
                clearInterval(timer);
              }
              
              if (finalNumber?.includes('$')) {
                numberElement.textContent = `$${Math.floor(currentNumber)}M+`;
              } else if (finalNumber?.includes('+')) {
                numberElement.textContent = `${Math.floor(currentNumber).toLocaleString()}+`;
              } else if (isPercentage) {
                numberElement.textContent = `${Math.floor(currentNumber)}%`;
              } else if (isDays) {
                numberElement.textContent = `${Math.floor(currentNumber)} Days`;
              }
            }, 50);
          }
        }
      });
    }, observerOptions);

    // Observe stat items
    const statItems = sectionRef.current?.querySelectorAll('.stat-item');
    statItems?.forEach((item, index) => {
      item.classList.add(`delay-${index * 100}`);
      observer.observe(item);
    });

    // Parallax effect
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrolled = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const parallaxSpeed = scrolled * 50;
        
        sectionRef.current.style.transform = `translateY(${parallaxSpeed}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const stats = [
    {
      icon: 'ri-star-fill',
      number: '500+',
      label: 'Five-Star Reviews',
      color: 'yellow-500',
      bgColor: 'yellow-50'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      number: '$50M+',
      label: 'Revenue Generated',
      color: 'green-500',
      bgColor: 'green-50'
    },
    {
      icon: 'ri-line-chart-line',
      number: '2,500+',
      label: 'Funnels Built',
      color: 'blue-500',
      bgColor: 'blue-50'
    },
    {
      icon: 'ri-heart-fill',
      number: '98%',
      label: 'Client Satisfaction',
      color: 'red-500',
      bgColor: 'red-50'
    },
    {
      icon: 'ri-timer-line',
      number: '7 Days',
      label: 'Average Delivery',
      color: 'purple-500',
      bgColor: 'purple-50'
    },
    {
      icon: 'ri-customer-service-2-line',
      number: '24/7',
      label: 'Support Available',
      color: 'indigo-500',
      bgColor: 'indigo-50'
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 bg-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full opacity-20 animate-pulse delay-1000"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Trusted by Thousands of Businesses
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our proven track record speaks for itself - real results for real businesses
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="stat-item text-center group hover:transform hover:scale-110 transition-all duration-500 opacity-0 translate-y-8"
            >
              <div className="mb-4">
                <div className={`w-16 h-16 mx-auto rounded-full bg-${stat.bgColor} flex items-center justify-center group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-purple-500 transition-all duration-500 shadow-lg group-hover:shadow-xl`}>
                  <i className={`${stat.icon} text-2xl text-${stat.color} group-hover:text-white transition-colors duration-500 group-hover:animate-bounce`}></i>
                </div>
              </div>
              <div className={`stat-number text-3xl sm:text-4xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}>
                {stat.number}
              </div>
              <div className="text-sm sm:text-base text-gray-600 font-medium group-hover:text-gray-900 transition-colors duration-300">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <i className="ri-shield-check-line text-green-600 mr-2 animate-pulse"></i>
            <span className="text-green-800 font-medium">100% Money Back Guarantee</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        .delay-0 { transition-delay: 0ms; }
        .delay-100 { transition-delay: 100ms; }
        .delay-200 { transition-delay: 200ms; }
        .delay-300 { transition-delay: 300ms; }
        .delay-400 { transition-delay: 400ms; }
        .delay-500 { transition-delay: 500ms; }
        .stat-item {
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
      `}</style>
    </section>
  );
}
